import type { OrderCategory, OrderSearchContext, OrderCategorySearchResult } from "@/shared/orders/resourceOrderSearch";
import { PossibleCalendarTypes } from "@/components/calendar/MainCalendar";
import BaseApiService from "@/shared/api/BaseApiService";
import request from "@/shared/api/request";
import { QueryFunctionContext, QueryKey } from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { receiveTransportOrderStatuses, transportStatusReadRevision } from "@/stores/transportOrderStatus";
import type { TransportStatusSource } from "@/stores/transportOrderStatus";
import type { RestoredAssignment } from "@/components/calendar/transportSyncPresentation";

export type PlantUtilizationData = {
  plants: { id: number; name: string }[];
  rows: { date: string; plantId: number; plant: string; group: string; subgroup: string; article: string; amount: number }[];
};

export default class ApiService extends BaseApiService {
  static searchResourceOrderCategories(context: OrderSearchContext, q: string, selectedTag?: number, resourceType?: OrderCategory["type"]) {
    return (): Promise<OrderCategorySearchResult> => request({
      url: `/data/resource-order-search/${context.type}/${context.id}`,
      method: "GET",
      params: { surface: "client", q, selectedTag, resourceType },
    });
  }

  static getPlantUtilization() {
    return (): Promise<PlantUtilizationData> => request({ url: "/data/plant-utilization", method: "GET" });
  }

  static getTicketAttachment(eventId: number, messageId: number, index: number): Promise<ArrayBuffer> {
    return request({
      url: `/data/ticket-attachment/${eventId}/${messageId}/${index}`,
      method: "GET", responseType: "arraybuffer", timeout: 30000,
    });
  }

  static getEntitiesWithFilter(entity: string, filter: { [field: string]: string | number | [string, number] }, v?: string) {
    let add = {};
    if (!!v) {
      add = { v };
    }
    return () =>
      request({
        url: "/entity/" + entity + "/",
        method: "GET",
        params: { filter: btoa(JSON.stringify(filter)), ...add },
      });
  }

  static getMyProjects() {
    return () =>
      request({
        url: "/data/myProjects",
        method: "GET",
      });
  }

  static getExternalMaterials(plant, au) {
    return () =>
      request({
        url: "/data/externalMaterial/" + plant + "/" + au,
        method: "GET",
      });
  }
  static getAmountList(plant) {
    return () =>
      request({
        url: "/data/amountList/" + plant,
        method: "GET",
      });
  }

  static getNotifications() {
    return (data: any) =>
      request({
        url: "/data/notifications",
        method: "GET",
      });
  }

  static getNotificationSettings() {
    return (data: any) =>
      request({
        url: "/data/notificationSettings",
        method: "GET",
      });
  }

  static setNotificationStatus() {
    return (data) =>
      request({
        url: "/data/setNotificationStatus",
        method: "POST",
        data,
      });
  }

  static setNotificationSetting() {
    return (data) =>
      request({
        url: "/data/setNotificationSetting",
        method: "POST",
        data,
      });
  }

  static getGalleryZip() {
    return (data: { file: string; name: string }[]) =>
      request({
        url: "/data/galleryZip/request",
        method: "POST",
        data,
      });
  }

  static getServiceEventsByUser() {
    return () =>
      request({
        url: "/data/serviceByUser",
        method: "GET",
      });
  }

  static getStoffstromEntriesByUser() {
    return () =>
      request({
        url: "/data/stoffstromByUser",
        method: "GET",
      });
  }

  static getServiceList(region: number) {
    return () =>
      request({
        url: "/data/serviceList/" + region,
        method: "GET",
      });
  }

  static getSupplierOrders(projectId: number) {
    return () =>
      request({
        url: "/data/supplierOrders/" + projectId,
        method: "GET",
      });
  }

  static uploadFileApp(opts?: { onProgress?: (fraction: number) => void }) {
    return (data: FormData) =>
      request({
        url: "/data/uploadImage",
        method: "POST",
        data,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: opts?.onProgress
          ? (e: AxiosProgressEvent) => {
              if (!opts.onProgress || !e.total) return;
              const fraction = e.loaded / e.total;
              opts.onProgress(fraction);
            }
          : undefined,
      });
  }

  static impersonate(userId: number) {
    return request({
      url: `/token/impersonate/${userId}`,
      method: "POST",
    });
  }

  static getResourceStatus(
    type: "HUMAN" | "CREW" | "RESOURCE",
    tag: number,
    region: number,
    filter:
      | { type: "dates"; dates: string[] }
      | {
          type: "phase";
          phase: number;
          startConstraint: string | null;
          endConstraint: string | null;
          exceptions: string[];
        },
  ) {
    return () =>
      request({
        url: "/data/resStatus/" + type + "/" + tag + "/" + region + "/" + btoa(JSON.stringify(filter)),
        method: "GET",
      });
  }

  static getNullErfasser(region: number) {
    return () =>
      request({
        url: "/data/nullErfasser/" + region,
        method: "GET",
      });
  }

  static getStoffstromMap() {
    return () =>
      request({
        url: "/data/stoffstrom",
        method: "GET",
      });
  }

  static getOrderDeadlines(region: number, date: string) {
    return () => request({ url: "/data/orderDeadlines/" + region + "/" + date, method: "GET" });
  }

  static getBestellfrist(region: number, date: string) {
    return () =>
      request({
        url: "/data/bestellfrist/" + region + "/" + date,
        method: "GET",
      });
  }

  static getBookingList(date: string, region: number) {
    return () =>
      request({
        url: "/data/bookingList/" + date + "/" + region,
        method: "GET",
      });
  }

  static getLiveMap(type: string, query: string, region: number) {
    return () =>
      request({
        url: "/data/jMap/" + region + "/" + type + "/" + query,
        method: "GET",
      });
  }

  static parseOrderConfirmation(file: File, possibleMixingPlants: string[]) {
    const data = new FormData();
    data.append("file", file);
    data.append("possibleMixingPlants", JSON.stringify(possibleMixingPlants));
    return request({
      url: "/parseOrderConfirmation/",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data,
    });
  }

  static sendOrder(jobId: string) {
    return () =>
      request({
        url: "/data/sendOrder/" + jobId,
        method: "POST",
        data: {},
      });
  }

  static routeInfo() {
    return (params: { queryKey: QueryKey }) => {
      const data = params.queryKey[1] as {
        job: number;
        jobPosition: null | any;
        article: number;
        position: number | null;
        easyTarget: "SITE" | "LAGER";
      };

      return request({
        url: "/data/routeInfo",
        method: "POST",
        data,
      });
    };
  }

  static assignDurations() {
    return (params) =>
      request({
        url: "/data/assignDurations",
        method: "POST",
        data: params.queryKey[1],
      });
  }

  static resetRejection() {
    return (data) =>
      request({
        url: "/data/resetRejection",
        method: "POST",
        data: data,
      });
  }

  static answerVacationRequest() {
    return (data) =>
      request({
        url: "/data/answerVacationRequest",
        method: "POST",
        data: data,
      });
  }

  static setPosition() {
    return (data) =>
      request({
        url: "/data/setPosition",
        method: "POST",
        data: data,
      });
  }

  static updateErfasser(bookingId: string) {
    return (erfasserId: number) =>
      request({
        url: "/data/updateErfasser/" + bookingId,
        method: "POST",
        data: { erfasserId },
      });
  }

  static setPlantOrderStatus(orderId: number) {
    return (status: string) =>
      request({
        url: "/data/setPlantOrderStatus/" + orderId + "/" + status,
        method: "POST",
        data: {},
      });
  }

  static setTransportOrderStatus(orderId: number) {
    return (status: string) =>
      request({
        url: "/data/setTransportOrderStatus/" + orderId + "/" + status,
        method: "POST",
        data: {},
      }).then((result) => {
        if (result.code !== 200) {
          throw new Error(result.data?.error || "Übernahme nach Transplan fehlgeschlagen. Status wurde nicht geändert.");
        }
        return result;
      });
  }

  static restoreTransportAssignment(orderId: number, assignmentId: number) {
    return (token: string): Promise<RestoredAssignment> => request({
      url: `/data/restoreTransportAssignment/${orderId}/${assignmentId}`,
      method: "POST", data: { token },
    }).then((result) => {
      if (result.code !== 200) throw new Error(result.data?.error || "Wiederherstellen in Transplan fehlgeschlagen. Bitte erneut versuchen.");
      return result.data;
    });
  }

  static getMaterialOrderHistory(projectId: number) {
    return () =>
      request({
        url: "/data/materialOrderHistory/" + projectId,
        method: "GET",
      });
  }

  static getBauhofArticles(projectId: string) {
    return () =>
      request({
        url: "/data/bauhofArticles/" + projectId,
        method: "GET",
      });
  }

  static getVacationRequests(region: number) {
    return () =>
      request({
        url: "/data/vacationRequests/" + region,
        method: "GET",
      });
  }

  static getBauhofOrders(regionId: number) {
    return () =>
      request({
        url: "/data/bauhofCalendar/" + regionId,
        method: "GET",
      });
  }

  static getTransportStatus(date: string, region: number) {
    return () =>
      request({
        url: "/data/getTransportStatus/" + date + "/" + region,
        method: "GET",
      });
  }

  static getOrderHistory(projectId: string) {
    return () =>
      request({
        url: "/data/orderHistory/" + projectId,
        method: "GET",
      });
  }

  static getExternalOrderHistory(orderId: string) {
    return () => request({ url: "/data/externalOrderHistory/" + orderId, method: "GET" });
  }

  static getOrderLocation(projectId: string, type: "JOB" | "ORDER") {
    return () =>
      request({
        url: "/data/orderLocation/" + projectId + "/" + type,
        method: "GET",
      });
  }

  static getJobKeys(phaseId: number) {
    return () =>
      request({
        url: "/data/jobKeys/" + phaseId,
        method: "GET",
      });
  }

  static getResourceKeys(resourceId: number, phaseId: number) {
    return () =>
      request({
        url: "/data/resourceKeys/" + resourceId + "/" + phaseId,
        method: "GET",
      });
  }

  static getOverdue(region: number, type: "ALL" | "MINE") {
    return () =>
      request({
        url: "/data/overdue/" + region + "/" + type,
        method: "GET",
      });
  }

  static getReady(region: number) {
    return () =>
      request({
        url: "/data/readyState/" + region,
        method: "GET",
      });
  }

  static getMachineTransport(region: number) {
    return () =>
      request({
        url: "/data/mt_transport/" + region,
        method: "GET",
      });
  }

  static getRealBookingRow(project: number, week: string) {
    return () =>
      request({
        url: "/data/projectBookings/" + project + "/" + week,
        method: "GET",
      });
  }

  static getAllRealBookings(project: number, settings: { staff: boolean; crews: boolean; humans: boolean; resources: boolean }) {
    return () =>
      request({
        url: "/data/jobBookings/" + project,
        method: "GET",
        params: {
          staff: settings.staff ? "include" : "exclude",
          crews: settings.crews ? "include" : "exclude",
          humans: settings.humans ? "include" : "exclude",
          resources: settings.resources ? "include" : "exclude",
        },
      });
  }

  static getJobStates(project: number) {
    return () =>
      request({
        url: "/data/jobStates/" + project,
        method: "GET",
      });
  }

  static getProjectRejections(project: number) {
    return () =>
      request({
        url: "/data/orderRejections/" + project,
        method: "GET",
      });
  }

  static getProjectVacations(project: number) {
    return () =>
      request({
        url: "/data/projectVacations/" + project,
        method: "GET",
      });
  }

  static getBauhofTransport(region: number) {
    return () =>
      request({
        url: "/data/mt_bauhof/" + region,
        method: "GET",
      });
  }

  static searchMaterials() {
    return (data: { search: string }) =>
      request({
        url: "/data/searchMaterials/" + encodeURIComponent(data.search.replace(/\//g, "~")),
        method: "GET",
      });
  }

  static changeCalendar() {
    return (data) =>
      request({
        url: "/data/changeCalendar",
        method: "POST",
        data,
      });
  }

  static updateCrew() {
    return (data) =>
      request({
        url: "/data/updateCrew",
        method: "POST",
        data,
      });
  }

  static setTransportStatus() {
    return (data) =>
      request({
        url: "/data/setTransportStatus",
        method: "POST",
        data,
      });
  }

  static setPlantStatus() {
    return (data) =>
      request({
        url: "/data/setPlantStatus",
        method: "POST",
        data,
      });
  }

  static changeKGPCalendar() {
    return (data) =>
      request({
        url: "/data/changeKGPCalendar",
        method: "POST",
        data,
      });
  }

  static uploadFile() {
    return (file) => {
      const data = new FormData();
      data.append("file", file);
      return request({
        url: "/data/uploadImage",
        method: "POST",
        data,
        headers: { "Content-Type": "multipart/form-data" },
      });
    };
  }

  static autoBook() {
    return (id) =>
      request({
        url: "/data/autoBooking/" + id,
        method: "POST",
      });
  }

  static getCalendar(type: PossibleCalendarTypes | "MATERIAL", reference: string | number | null, month: string, region: number) {
    return async () => {
      const revision = transportStatusReadRevision();
      const data = await request({
        url: "/data/calendar/" + type + "/" + reference + "/" + month + "/" + region,
        method: "GET",
        params: { client: "browser" },
      });
      if (type === "MATERIAL") receiveTransportOrderStatuses(data.map((row: { materialOrder?: TransportStatusSource }) => row.materialOrder), revision);
      return data;
    };
  }

  static getDispoCal(date: string, region: number) {
    return async () => {
      const revision = transportStatusReadRevision();
      const data = await request({
        url: "/data/dispoCal/" + date + "/" + region,
        method: "GET",
      });
      receiveTransportOrderStatuses(data.flatMap((job: { materialOrders: TransportStatusSource[] }) => job.materialOrders), revision);
      return data;
    };
  }

  static getDispoCalJobIds(date: string, region: number) {
    return () =>
      request({
        url: "/data/dispoCalIds/" + date + "/" + region,
        method: "GET",
      });
  }

  static getDispoCalJobById(jobId: number) {
    return async () => {
      const revision = transportStatusReadRevision();
      const data = await request({
        url: "/data/dispoCalJob/" + jobId,
        method: "GET",
      });
      receiveTransportOrderStatuses(data.materialOrders, revision);
      return data;
    };
  }

  static getKGP(reference: number, date: string, region: number) {
    return () =>
      request({
        url: "/data/kgp/" + reference + "/" + date + "/" + region,
        method: "GET",
      });
  }

  static getJonasKalender(month: string, region: number, active: string[]) {
    return async () => {
      const revision = transportStatusReadRevision();
      const data = await request({
        url: "/data/jonaskalender/" + month + "/" + region + "/" + active.join(","),
        method: "GET",
      });
      const overview = data as Record<string, Record<string, { orders?: TransportStatusSource[] }>>;
      receiveTransportOrderStatuses(Object.values(overview).flatMap((jobs) => Object.values(jobs).flatMap((job) => job.orders ?? [])), revision);
      return data;
    };
  }

  static getOpenRequests(region: number, dispoCategory: number, type: "LONG" | "SHORT") {
    return () =>
      request({
        url: "/data/open/" + region + "/" + dispoCategory + "/" + type,
        method: "GET",
      });
  }

  static getCollisions(region: number, dispoCategory: number) {
    return () =>
      request({
        url: "/data/collisions/" + region + "/" + dispoCategory,
        method: "GET",
      });
  }

  static acceptCollisions() {
    return (data: { targetId: number; type: string; dates: string[] }) =>
      request({ url: "/data/collisions/accept", method: "POST", data });
  }

  static deleteCollisionBookings() {
    return (data: { bookingIds: number[] }) =>
      request({ url: "/data/collisions/bookings", method: "DELETE", data });
  }

  static getFreeRequests(region: number, dispoCategory: number, week: string) {
    return () =>
      request({
        url: "/data/free/" + region + "/" + dispoCategory + "/" + week,
        method: "GET",
      });
  }

  static getDailyTransfers(region: number, currentDate: string) {
    return () =>
      request({
        url: "/data/dailyTransfers/" + currentDate + "/" + region,
        method: "GET",
      });
  }

  static getMap(region: number) {
    return () =>
      request({
        url: "/data/map/" + region,
        method: "GET",
      });
  }

  static getEntitiesByTag(entity: BackendEntity, tag: number, region: number) {
    return () =>
      request({
        url: "/entityByTag/" + entity + "/" + tag + "/" + region,
        method: "GET",
      });
  }
}
